import { ContractFileCategory, ContractFileResponse } from 'compliance-api/generated';
import { useMemo, useState } from 'react';
import {
  contractFileCategoryLabels,
  getContractFileCategory,
  getContractFileName,
} from '@/modules/admin-dashboard/components/contractManagement/ContractByID/contractFiles.ts';
import {
  useDeleteContractFileMutation,
  useUpdateContractFileMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { Button, DropdownMenu, DropdownMenuItem } from 'ui';
import {
  ArrowDownBlackIcon,
  ArrowUpBlackIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
  TrashGrayIcon,
  UploadWhiteIcon,
} from 'ui/icons';

interface ItemFileProps {
  file: ContractFileResponse;
  isActive: boolean;
  contractId: number;
  onSelectFile: (fileId: number) => void;
  readOnly: boolean;
}

interface MoreButtonProps {
  file: ContractFileResponse;
  contractId: number;
}

interface ContractFilesSidebarProps {
  files: ContractFileResponse[];
  contractId: number;
  activeFileId?: number;
  onSelectFile: (fileId: number) => void;
  onUploadFiles: () => void;
  readOnly: boolean;
}

export const ContractFilesSidebar = ({
  files,
  contractId,
  activeFileId,
  onSelectFile,
  onUploadFiles,
  readOnly,
}: ContractFilesSidebarProps) => {
  const [expandedCategories, setExpandedCategories] = useState<
    Record<ContractFileCategory, boolean>
  >({
    [ContractFileCategory.CoreDocument]: true,
    [ContractFileCategory.SupportingDocument]: true,
  });

  const filesByCategory = useMemo(
    () =>
      files.reduce<Record<ContractFileCategory, ContractFileResponse[]>>(
        (acc, file) => {
          acc[getContractFileCategory(file)].push(file);
          return acc;
        },
        {
          [ContractFileCategory.CoreDocument]: [],
          [ContractFileCategory.SupportingDocument]: [],
        },
      ),
    [files],
  );

  const handleToggleCategory = (category: ContractFileCategory) => {
    setExpandedCategories(value => ({
      ...value,
      [category]: !value[category],
    }));
  };

  return (
    <aside className="w-full shrink-0 border-b border-gray-200 bg-white lg:h-[calc(100vh-71px)] lg:w-[240px] lg:border-b-0 lg:border-r">
      <div className="flex h-full flex-col overflow-y-auto px-4 py-4">
        <Button
          className="h-9 min-h-[36px] w-full gap-2 rounded px-3"
          onClick={onUploadFiles}
          disabled={readOnly}
        >
          <img src={UploadWhiteIcon} alt="" className="h-5 w-5" />
          Upload new files
        </Button>

        <div className="mt-6 space-y-8">
          {Object.values(ContractFileCategory).map(category => {
            const categoryFiles = filesByCategory[category];
            const isExpanded = expandedCategories[category];

            return (
              <div key={category}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left text-xs font-medium uppercase leading-5 text-gray-500"
                  onClick={() => handleToggleCategory(category)}
                >
                  <img
                    src={ChevronDownIcon}
                    alt=""
                    className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                  />
                  <span>{contractFileCategoryLabels[category]}</span>
                  <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs leading-4 text-gray-500">
                    {categoryFiles.length}
                  </span>
                </button>

                {isExpanded && (
                  <div className="mt-2">
                    {categoryFiles.map(file => (
                      <ItemFile
                        key={file.id}
                        file={file}
                        contractId={contractId}
                        isActive={file.id === activeFileId}
                        onSelectFile={onSelectFile}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

const ItemFile = ({ file, isActive, contractId, onSelectFile, readOnly }: ItemFileProps) => {
  return (
    <div
      className={`flex items-center gap-1 border-l-2 px-3 py-2 transition-colors ${
        isActive
          ? 'border-blue-500 bg-blue-50 text-gray-900'
          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left text-sm leading-5"
        onClick={() => onSelectFile(file.id)}
      >
        {getContractFileName(file)}
      </button>
      {!readOnly && <MoreButton file={file} contractId={contractId} />}
    </div>
  );
};

const MoreButton = ({ file, contractId }: Omit<MoreButtonProps, 'readOnly'>) => {
  const { mutateAsync: updateContractFile } = useUpdateContractFileMutation();
  const { mutateAsync: deleteContractFile } = useDeleteContractFileMutation();
  const fileCategory = getContractFileCategory(file);
  const menuItems: DropdownMenuItem[] = [
    fileCategory === ContractFileCategory.CoreDocument
      ? {
          label: 'Move to supporting',
          icon: ArrowDownBlackIcon,
          onClick: () =>
            void updateContractFile({
              contractId,
              fileId: file.id,
              data: {
                file_category: ContractFileCategory.SupportingDocument,
              },
            }),
        }
      : {
          label: 'Promote to core',
          icon: ArrowUpBlackIcon,
          onClick: () =>
            void updateContractFile({
              contractId,
              fileId: file.id,
              data: {
                file_category: ContractFileCategory.CoreDocument,
              },
            }),
        },
    {
      label: 'Delete document',
      icon: TrashGrayIcon,
      onClick: () =>
        void deleteContractFile({
          contractId,
          fileId: file.id,
        }),
    },
  ];

  return (
    <DropdownMenu
      items={menuItems}
      button={
        <button type="button" className="flex items-center justify-center">
          <img alt="actions" src={DotsHorizontalIcon} className="h-6 w-6 min-h-6 min-w-6" />
        </button>
      }
      className="w-[210px] divide-white py-1"
      containerClassName="w-fit shrink-0"
      iconItemsWithBorder={false}
    />
  );
};
